import { CSSProperties, useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/theme/ThemeContext";
import { Avatar } from "@/components/Avatar";
import { BrandLockup } from "@/components/Brand";
import { CommandPalette } from "@/components/CommandPalette";
import { FloatingProgressPanel } from "@/components/PublishProgress";
import { SupportButton } from "@/components/SupportButton";
import { TrialBanner } from "@/components/TrialBanner";
import { ChatProvider, ChatShell, ChatTrigger } from "@/messaging/chat";
import { NotificationsBell, NotificationsProvider } from "@/messaging/notifications";
import { LeaderTicker } from "@/components/LeaderTicker";
import { TierProvider } from "@/lib/tierContext";
import { AvatarPickerModal } from "@/pages/Profile";
import {
  LogoutIcon,
  MoonIcon,
  SearchIcon,
  SunIcon,
  UserCircleIcon,
  isGroup,
  visibleSections,
  type NavGroup,
  type NavItem,
} from "@/components/nav";
import type { CurrentUser } from "@/types";
import { roleLabel } from "@/types";

// One fixed rail width. The sidebar used to collapse to a 68px icon rail;
// that mode is gone, so the width is a constant and <main> just offsets by it.
const SIDEBAR_W = 248;
// Persisted expansion state for the one remaining nav group (Publish).
const NAV_GROUPS_KEY = "agencygrid.sidebar.groups";

export function Layout() {
  const { user, loading, logout, refresh } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const loc = useLocation();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Hoisted chat state. The drawer renders once at the bottom; the trigger
  // lives in two places (mobile top bar + desktop top bar) and shares this
  // state so unread counts stay in lockstep.

  // Nav-group expansion, persisted across reloads. ``undefined`` for a key
  // means "the user hasn't decided", which lets the active-child auto-expand
  // take over; an explicit false must be able to beat that.
  const [expandedGroups, setExpandedGroups] = useState<Map<string, boolean>>(() => {
    if (typeof window === "undefined") return new Map();
    try {
      const raw = window.localStorage.getItem(NAV_GROUPS_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
        return new Map(parsed.map((k) => [k, true]));
      }
      return new Map(parsed as Array<[string, boolean]>);
    } catch {
      return new Map();
    }
  });
  useEffect(() => {
    window.localStorage.setItem(
      NAV_GROUPS_KEY,
      JSON.stringify([...expandedGroups.entries()]),
    );
  }, [expandedGroups]);

  // Close the mobile drawer whenever the route changes — tapping a nav link
  // should advance and dismiss the overlay in one gesture.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [loc.pathname]);

  // Body scroll-lock + Escape-to-close while the drawer is open. Without the
  // lock, swipes inside the drawer scroll the page underneath.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileNavOpen]);

  // ⌘K / Ctrl-K from anywhere. Registered here (not in the palette) so the
  // shortcut works while the palette is closed, and toggles it while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-600">
          loading…
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }

  // Every teammate needs an avatar — block the whole app behind an
  // undismissable picker until one's set, rather than letting people
  // wander around as a bare initials circle forever.
  // …unless the avatar is admin-pinned: PATCH /me would 403 for those users,
  // so the gate could never be satisfied. (A locked user normally has a URL
  // already; this is belt-and-braces.)
  if (!user.avatar_url && !user.avatar_locked) {
    return (
      <AvatarPickerModal
        currentUrl={null}
        name={user.full_name}
        email={user.email}
        required
        onClose={() => {}}
        onLogout={logout}
        onSave={async (url) => {
          await api("/api/auth/me", {
            method: "PATCH",
            json: { avatar_url: url ?? "" },
          });
          await refresh();
        }}
      />
    );
  }

  // Sections, filtered by the server-resolved page permissions. Empty
  // sections (and their headings) drop out entirely — see components/nav.tsx.
  const sections = visibleSections(new Set(user.allowed_pages));

  const cssVars = { "--sidebar-w": `${SIDEBAR_W}px` } as CSSProperties;
  // 100dvh tracks the *visible* viewport on iOS — with Chrome's bottom URL bar
  // showing, 100vh would push the account block below the fold.
  const sidebarStyle: CSSProperties = { ...cssVars, height: "100dvh" };

  return (
    // WSProvider lives above PublishProgressProvider in App.tsx (it needs to be
    // an ancestor of the publish-progress context, not just of Layout) — this
    // component consumes it via the WS + publish-progress contexts.
    <ChatProvider>
    <NotificationsProvider>
    {/* Inside the auth gate: the tier map is agency-scoped and the public
        share pages must never call it. */}
    <TierProvider>
    <div className="min-h-screen bg-ink-950">
        {/* ─── Mobile top bar ─── */}
        <header className="lg:hidden sticky top-0 z-30 bg-ink-900/85 backdrop-blur-md border-b border-white/5 safe-pt">
          <div className="h-14 px-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
              className="icon-btn -ml-1"
            >
              <HamburgerIcon />
            </button>
            <Link
              to="/home"
              className="flex items-center justify-center min-w-0 flex-1"
              aria-label="Meridian — home"
            >
              <BrandLockup />
            </Link>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search"
                className="icon-btn"
              >
                <SearchIcon />
              </button>
              <NotificationsBell />
              <ChatTrigger />
            </div>
          </div>
        </header>

        {/* ─── Mobile drawer backdrop ─── */}
        {mobileNavOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/55 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
        )}

        {/* ─── Sidebar — drawer on mobile, persistent on lg+ ─── */}
        <aside
          style={sidebarStyle}
          className={`fixed top-0 left-0 bg-ink-900/95 lg:bg-ink-900/80 backdrop-blur-sm border-r border-white/5 flex flex-col z-50 lg:z-40 transition-transform duration-300 ease-in-out safe-pt
            w-[280px] lg:w-[var(--sidebar-w)]
            ${mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        >
          {/* Brand header. ``lg:h-14`` exactly matches the desktop top bar so
              the two bottom borders land on the same horizontal line and the
              chrome reads as one continuous rule. */}
          <div className="border-b border-white/5 flex-shrink-0 flex items-center px-5 py-4 lg:py-0 lg:h-14">
            <Link to="/home" aria-label="Meridian — home">
              <BrandLockup />
            </Link>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close menu"
              className="icon-btn lg:hidden ml-auto -mr-1"
            >
              <CloseIcon />
            </button>
          </div>

          <nav className="flex-1 px-3 py-3 overflow-y-auto overflow-x-hidden scrollbar-thin">
            {sections.map((section) => (
              <div key={section.key} className="mb-0.5">
                {section.label && (
                  <div className="px-3 mt-5 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                    {section.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {section.entries.map((entry) =>
                    isGroup(entry) ? (
                      <GroupRow
                        key={entry.key}
                        group={entry}
                        expanded={
                          expandedGroups.get(entry.key) ??
                          entry.children.some(
                            (c) =>
                              loc.pathname === c.to ||
                              loc.pathname.startsWith(c.to + "/"),
                          )
                        }
                        onToggle={(next) =>
                          setExpandedGroups((prev) => {
                            const m = new Map(prev);
                            m.set(entry.key, next);
                            return m;
                          })
                        }
                      />
                    ) : (
                      <ItemRow key={entry.to} item={entry} pathname={loc.pathname} />
                    ),
                  )}
                </div>
              </div>
            ))}
          </nav>

          {/* Account block — pinned to the bottom edge on both breakpoints,
              replacing the old top-bar user menu and the mobile-only footer. */}
          <AccountBlock
            user={user}
            theme={theme}
            onToggleTheme={toggleTheme}
            logout={logout}
          />
        </aside>

        <main
          style={cssVars}
          className="min-h-screen flex flex-col lg:ml-[var(--sidebar-w)]"
        >
          {/* Desktop top bar — search on the left (the empty half it used to
              be), messaging cluster on the right. A real strip rather than a
              floating overlay, so page-level buttons are never occluded. */}
          <div className="hidden lg:flex sticky top-0 z-30 h-14 px-8 items-center gap-3 bg-ink-950/80 backdrop-blur border-b border-white/5">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="group flex items-center gap-2.5 h-9 w-full max-w-sm px-3 rounded-lg bg-ink-900/60 border border-white/10 hover:border-white/20 transition text-left"
            >
              <span className="text-slate-500 group-hover:text-slate-400 transition">
                <SearchIcon />
              </span>
              <span className="flex-1 text-[13px] text-slate-500 group-hover:text-slate-400 transition">
                Search or jump to…
              </span>
              <kbd className="text-[10px] font-semibold text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
                ⌘K
              </kbd>
            </button>
            <LeaderTicker />
            <div className="ml-auto flex items-center gap-0.5 pl-2 border-l border-white/5">
              <NotificationsBell />
              <ChatTrigger />
              <SupportButton />
            </div>
          </div>
          <TrialBanner agency={user.agency} />
          <div className="flex-1 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 safe-pb">
            <Outlet />
          </div>
        </main>

        {/* Single chat drawer — owned by Layout so both triggers share the
            same open/unread state. */}
        <ChatShell />

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

        {/* Floating publish-progress card — fixed bottom-right, mounted in
            Layout so it persists across navigation. Returns null when no jobs
            are active, so it costs nothing on idle pages. */}
        <FloatingProgressPanel />
      </div>
    </TierProvider>
    </NotificationsProvider>
    </ChatProvider>
  );
}

// ─── Nav rows ──────────────────────────────────────────────────────────────

function ItemRow({ item, pathname }: { item: NavItem; pathname: string }) {
  const extraActive =
    item.activeIfStartsWith?.some((p) => pathname.startsWith(p)) ?? false;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `nav-link ${isActive || extraActive ? "nav-link-active" : ""}`
      }
    >
      <item.icon />
      <span className="whitespace-nowrap">{item.label}</span>
    </NavLink>
  );
}

/** Expandable group (only Publish survives the section rework — its children
 *  are modes of one task). Groups with a single visible child are flattened to
 *  a plain link upstream in ``visibleSections``, so this always has ≥2. */
function GroupRow({
  group,
  expanded,
  onToggle,
}: {
  group: NavGroup;
  expanded: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(!expanded)}
        aria-expanded={expanded}
        className="nav-link w-full text-left"
      >
        <group.icon />
        <span className="whitespace-nowrap flex-1">{group.label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`flex-shrink-0 text-slate-500 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded && (
        <div className="mt-0.5 ml-5 pl-3 border-l border-white/5 space-y-0.5">
          {group.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end
              className={({ isActive }) =>
                `nav-link !text-[12.5px] ${isActive ? "nav-link-active" : ""}`
              }
            >
              <span className="whitespace-nowrap">{child.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Account ───────────────────────────────────────────────────────────────


/** Avatar + name + role at the sidebar's bottom edge, opening a menu upward.
 *  Consolidates what used to be three separate surfaces: the top-bar user
 *  menu, the mobile drawer's profile block, and the duplicated theme toggle. */
function AccountBlock({
  user,
  theme,
  onToggleTheme,
  logout,
}: {
  user: CurrentUser;
  theme: string;
  onToggleTheme: () => void;
  logout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0 p-3 border-t border-white/5 safe-pb">
      {open && (
        <div
          role="menu"
          className="absolute bottom-[calc(100%-0.25rem)] left-3 right-3 rounded-xl bg-ink-850 border border-white/10 shadow-2xl shadow-black/40 overflow-hidden"
        >
          <div className="px-3 py-2.5 border-b border-white/5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Agency
            </div>
            <div className="text-[13px] font-semibold text-slate-200 truncate">
              {user.agency.name}
            </div>
          </div>
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-slate-200 hover:bg-white/[0.05] transition"
          >
            <UserCircleIcon />
            <span>Profile</span>
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onToggleTheme();
              setOpen(false);
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-slate-200 hover:bg-white/[0.05] transition text-left"
          >
            {theme === "light" ? <MoonIcon /> : <SunIcon />}
            <span>{theme === "light" ? "Dark theme" : "Light theme"}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-rose-300 hover:bg-rose-500/10 transition text-left border-t border-white/5"
          >
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-xl transition border ${
          open
            ? "bg-white/[0.06] border-white/15"
            : "border-transparent hover:bg-white/[0.04]"
        }`}
      >
        <Avatar
          url={user.avatar_url}
          name={user.full_name}
          email={user.email}
          size="sm"
          online={user.is_online}
          userId={user.id}
        />
        <span className="min-w-0 flex-1 text-left">
          <span className="block text-[13px] font-medium text-slate-200 truncate">
            {user.full_name || user.email}
          </span>
          <span className="block text-[11px] text-slate-500 truncate">
            {roleLabel(user.role)}
          </span>
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`flex-shrink-0 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

// ─── Chrome icons (the nav's own set lives in components/nav.tsx) ──────────

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
